import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import Loading from 'src/components/loading';
import { useAggregation } from 'src/hooks/useAggregation';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import {
  useInsertAppetite,
  useUpdateAppetite,
} from '@/hooks/mutations/appetite';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetAppetiteById, useGetRiskById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import type { CreatedEntityDestination } from '@/utils/createWithFiles';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { appetiteDetailsUrl } from '@/utils/urls';

import AppetiteForm from '../forms/AppetiteForm';
import type { AppetiteFormFieldsData } from '../forms/appetiteSchema';

type Props = {
  Id?: string;
  ParentId?: string;
};

const Tab: FC<Props> = ({ Id, ParentId }) => {
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const { updateFiles } = useFileUpdate();
  const { appetiteAggregation, loading: aggregationLoading } = useAggregation();
  const { data: riskData, loading: risksLoading } = useGetRiskById({
    queryArgs: { riskId: ParentId ?? '' },
    shouldSkip: !ParentId,
  });
  const navigate = useNavigate();
  const parentRisk = riskData?.risk[0];

  const { insertAppetite: insert } = useInsertAppetite();
  const { updateAppetite: update } = useUpdateAppetite();
  // Resume file retries, but never carry an ID or late async result across parents.
  const createdAppetiteRef = useRef<
    { parentRiskId: string; appetiteId: string } | undefined
  >(undefined);
  const currentParentRiskIdRef = useRef(parentRisk?.Id);
  currentParentRiskIdRef.current = parentRisk?.Id;
  useEffect(() => {
    const parentRiskId = parentRisk?.Id;
    currentParentRiskIdRef.current = parentRiskId;

    return () => {
      if (currentParentRiskIdRef.current === parentRiskId) {
        currentParentRiskIdRef.current = undefined;
      }
    };
  }, [parentRisk?.Id]);
  if (createdAppetiteRef.current?.parentRiskId !== parentRisk?.Id) {
    createdAppetiteRef.current = undefined;
  }
  const onDismiss = () => {
    if (currentParentRiskIdRef.current === parentRisk?.Id) {
      navigate(-1);
    }
  };

  const {
    data: appetiteData,
    loading,
    error,
  } = useGetAppetiteById({
    queryArgs: { id: Id ?? '' },
    shouldSkip: !Id,
  });
  if (error) {
    throw error;
  }
  const appetite = appetiteData?.appetite[0];

  const values = useMemo(() => {
    if (!appetite) {
      return undefined;
    }

    const fields = {
      ...appetite,
      files: appetite?.files.map((rf) => rf.file),
      ImpactId: appetite.impact?.Id,
      AppetiteType: appetite.AppetiteType,
    };

    return fields;
  }, [appetite]);

  const onSave = async (data: AppetiteFormFieldsData) => {
    const saveParentRiskId = parentRisk?.Id;
    try {
      const { files, ...rest } = data;
      const retainedAppetite =
        createdAppetiteRef.current?.parentRiskId === saveParentRiskId
          ? createdAppetiteRef.current
          : undefined;
      let savedAppetiteId = values?.Id ?? retainedAppetite?.appetiteId;
      let createdEntity: CreatedEntityDestination | undefined =
        !values && savedAppetiteId
          ? {
              id: savedAppetiteId,
              destination: appetiteDetailsUrl(savedAppetiteId),
            }
          : undefined;
      if (values) {
        const result = await update({
          ...rest,
          Id: values.Id,
          OriginalTimestamp: values.ModifiedAtTimestamp,
          CustomAttributeData: data.CustomAttributeData || undefined,
        });
        if (result.update_appetite?.affected_rows !== 1) {
          throw new Error(
            'Records not updated. Record may have been updated by another user'
          );
        }
      } else if (!savedAppetiteId) {
        if (!saveParentRiskId) {
          throw new Error('Cannot insert acceptance without a parent risk');
        }

        const result = await insert({
          ...rest,
          ParentIds: [saveParentRiskId],
          CustomAttributeData: data.CustomAttributeData || undefined,
        });
        savedAppetiteId = result.insertChildAppetite?.Id;
        if (savedAppetiteId) {
          if (currentParentRiskIdRef.current === saveParentRiskId) {
            createdAppetiteRef.current = {
              parentRiskId: saveParentRiskId,
              appetiteId: savedAppetiteId,
            };
          }
          createdEntity = {
            id: savedAppetiteId,
            destination: appetiteDetailsUrl(savedAppetiteId),
          };
        }
      }
      if (!savedAppetiteId) {
        throw new Error('Missing id');
      }
      const saveFiles = () =>
        updateFiles({
          parentType: Parent_Type_Enum.Appetite,
          parentId: savedAppetiteId,
          originalFiles: values?.files,
          selectedFiles: files,
        });
      await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

      if (currentParentRiskIdRef.current !== saveParentRiskId) {
        return MUTATION_NO_OP;
      }

      return { savedIds: [savedAppetiteId], createdEntity };
    } catch (error) {
      if (currentParentRiskIdRef.current !== saveParentRiskId) {
        return MUTATION_NO_OP;
      }
      throw error;
    }
  };

  const { hasPermission: canEditAppetite, loading: isLoadingEditAppetite } =
    useHasPermissionQuery('update:appetite', appetite);
  const { hasPermission: canCreateAppetite, loading: isLoadingCreateAppetite } =
    useHasPermissionQuery('insert:appetite', parentRisk);
  const loadingPermissions = isLoadingEditAppetite || isLoadingCreateAppetite;
  const { enableTierTwoCascading } = useAggregation();

  const canModify = appetite ? canEditAppetite : canCreateAppetite;

  if (loadingPermissions || loading || aggregationLoading || risksLoading) {
    return <Loading />;
  }

  const isTierThree = parentRisk?.Tier === 3;
  const isTierTwo = parentRisk?.Tier === 2;

  return (
    <>
      <AppetiteForm
        appetiteAggregation={appetiteAggregation}
        onSave={onSave}
        values={values}
        onDismiss={onDismiss}
        publishToolbarState={objectToolbarEnabled}
        publishActiveForm={formPrefillEnabled}
        activeFormRecordId={Id ?? null}
        readOnly={
          !canModify ||
          (appetiteAggregation !== 'default' &&
            (isTierThree || (isTierTwo && !enableTierTwoCascading)))
        }
      />
    </>
  );
};

export default Tab;
